/**
 * shared/errors/opportunityErrors — mapa canônico de códigos OP-ERR-* do
 * opportunity-pipeline para mensagens pt-BR. Ponto único para evitar divergência
 * de mensagens entre as telas (criar/editar/perder/avançar).
 *
 * Alguns códigos são contextuais (ex.: OP-ERR-011/012 têm leitura diferente no
 * fluxo de perda) — esses casos são tratados via `overrides` por chamador, sem
 * duplicar a tabela base.
 */

import { ApiError } from "../../services/ApiClient.js";

/** Mensagens canônicas por código OP-ERR-*. */
const OP_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  "OP-ERR-001": "Dados obrigatórios ausentes (título, conta, estágio ou canal).",
  "OP-ERR-002": "Responsável inválido ou fora da BU.",
  "OP-ERR-003": "Canal de origem inválido. Verifique o canal selecionado.",
  "OP-ERR-004": "Canal é de parceiro, mas o parceiro não foi informado.",
  "OP-ERR-005": "Data de fechamento é obrigatória para avançar neste estágio.",
  "OP-ERR-006": "Motivo de perda é obrigatório.",
  "OP-ERR-007": "Transição de estágio inválida.",
  "OP-ERR-010": "Conta não encontrada ou fora do escopo da BU.",
  "OP-ERR-018": "Não foi possível concluir agora: um serviço interno não respondeu. Tente novamente em instantes.",
  // OP-ERR-020 existe porque esta validação usava o OP-ERR-018 antes de ele virar
  // indisponibilidade: quem reatribuía carteira para o próprio dono lia "um serviço interno não
  // respondeu" e tentava de novo para sempre.
  "OP-ERR-020": "Escolha um responsável de destino diferente do atual.",
  "OP-ERR-019": "Não foi possível concluir: houve um erro interno na comunicação entre serviços. Repetir não resolve — acione o suporte.",
  "OP-ERR-011": "O título não pode estar vazio.",
  "OP-ERR-012": "Duração em meses é obrigatória quando há valor mensal.",
  "OP-ERR-013": "Esta transição de estágio não é permitida no fluxo configurado.",
};

export interface MapOpportunityErrorOptions {
  /** Mensagens específicas do contexto que sobrepõem o mapa canônico (por código). */
  readonly overrides?: Readonly<Record<string, string>>;
  /** Mensagem padrão quando o código não está mapeado e não há `message` no erro. */
  readonly fallback?: string;
}

/**
 * Traduz um erro (idealmente `ApiError` com `code` OP-ERR-*) para mensagem pt-BR.
 * Precedência: override do contexto → mapa canônico → `err.message` → fallback.
 */
export function mapOpportunityError(
  err: unknown,
  options: MapOpportunityErrorOptions = {},
): string {
  const { overrides = {}, fallback = "Erro ao processar a oportunidade. Tente novamente." } = options;
  if (err instanceof ApiError) {
    // `|| fallback` (não `??`) para que message vazia ("") também caia no fallback.
    return overrides[err.code] ?? OP_ERROR_MESSAGES[err.code] ?? (err.message || fallback);
  }
  if (err instanceof Error) return err.message;
  return "Erro inesperado. Tente novamente.";
}
